import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { auth } from '../lib/api';

export default function Setup() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    auth.setupStatus().then(({ needsBootstrap }) => setNeedsBootstrap(needsBootstrap)).finally(() => setChecking(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await auth.bootstrapAdmin(email, password, fullName);
      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600" /></div>;
  }

  if (!needsBootstrap) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="card text-center max-w-md w-full space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">Setup already completed</h2>
          <p className="text-gray-500">This ExpenseEasy instance already has a platform administrator. Ask them to add your business or account.</p>
          <Link to="/login" className="btn-primary inline-block px-8">Go to Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Wallet className="w-7 h-7 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Set up ExpenseEasy</h1>
          <p className="text-gray-500 mt-1">Create the platform administrator account. This is a one-time step.</p>
        </div>

        <div className="card space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Full name</label>
              <input type="text" className="input-field" value={fullName} onChange={e => setFullName(e.target.value)} />
            </div>
            <div>
              <label className="label">Email address</label>
              <input type="email" className="input-field" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="label">Password</label>
              <input type="password" className="input-field" placeholder="Min. 8 characters" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} />
            </div>
            <button type="submit" className="w-full btn-primary py-2.5" disabled={loading}>
              {loading ? 'Creating...' : 'Create Platform Admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
